import React, { useEffect, useMemo } from 'react';
import { Spin, Form, Input, Select, DatePicker, Button, Typography } from 'antd';
import Drawer from '../../../components/AppDrawer';
import dayjs from 'dayjs';
import { useCreateTaskItem } from '../hooks/useCreateTaskItem';
import { useUpdateTaskItem } from '../hooks/useUpdateTaskItem';
import { useTaskItem } from '../hooks/useTaskItem';
import {
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_OPTIONS,
  TASK_TYPE_BY_API_NAME,
  TASK_TYPE_OPTIONS,
  TASK_TYPE_LABELS,
  TASK_TYPE_FOLLOWUP,
  TASK_TYPE_INTERNAL,
  TASK_TYPE_RECURRING,
  isUnlinkedTaskType,
} from '../constants';
import { coerceEnumByLabel } from '../../../lib/api/coerceEnum';
import { useUserOptions } from '../../../hooks/options/useUserOptions';
import { useContactOptions } from '../../../hooks/options/useContactOptions';
import { useLeadOptions } from '../../../hooks/options/useLeadOptions';
import { useAuthStore } from '../../auth/store/authStore';
import { usePermission } from '../../permissions/PermissionsContext';
import { contactDisplayName } from '../../contacts/contactType';
import type { TaskFormValues, TaskPriority, TaskType } from '../types';

const TASK_PRIORITY_BY_LABEL = Object.fromEntries(
  Object.entries(TASK_PRIORITY_LABELS).map(([value, label]) => [label, Number(value) as TaskPriority]),
) as Record<string, TaskPriority>;

export interface TaskFormDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editTaskId?: string | null;
  defaultTaskType?: TaskType;
  initialContactId?: string;
  initialLeadId?: string;
  lockContactAndLead?: boolean;
  lockContact?: boolean;
}

const TaskFormDrawer: React.FC<TaskFormDrawerProps> = ({
  open,
  onClose,
  onSuccess,
  editTaskId,
  defaultTaskType = TASK_TYPE_INTERNAL,
  initialContactId,
  initialLeadId,
  lockContactAndLead = false,
  lockContact = false,
}) => {
  const contactLocked = lockContactAndLead || lockContact;
  const leadLocked = lockContactAndLead;
  const [form] = Form.useForm();
  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = usePermission('administrator.access');
  const isEdit = Boolean(editTaskId);
  const createMutation = useCreateTaskItem(onSuccess);
  const updateMutation = useUpdateTaskItem(editTaskId ?? null, onSuccess);
  const { data: task, isLoading } = useTaskItem(open && editTaskId ? editTaskId : null);
  const { data: users = [] } = useUserOptions();
  const { data: contacts = [] } = useContactOptions();

  const taskType = Form.useWatch('taskType', form) as TaskType | undefined;
  const contactId = Form.useWatch('contactId', form) as string | undefined;

  const { data: leads = [] } = useLeadOptions(
    { contactId: taskType === TASK_TYPE_FOLLOWUP ? contactId : undefined },
    taskType === TASK_TYPE_FOLLOWUP && Boolean(contactId),
  );

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: u.label,
      })),
    [users]
  );

  const assigneeFallbackOption = useMemo(
    () =>
      isEdit && task?.assigneeId
        ? { value: task.assigneeId, label: task.assigneeDisplayName ?? task.assigneeId }
        : null,
    [isEdit, task?.assigneeId, task?.assigneeDisplayName]
  );

  const currentUserOption = useMemo(
    () => currentUser ? { value: currentUser.id, label: `${currentUser.firstName} ${currentUser.lastName}` } : null,
    [currentUser]
  );

  const allUserOptions = useMemo(
    () => {
      if (!isAdmin && currentUserOption) {
        return [currentUserOption];
      }
      if (!assigneeFallbackOption) return userOptions;
      const exists = userOptions.some((o) => o.value === assigneeFallbackOption.value);
      return exists ? userOptions : [assigneeFallbackOption, ...userOptions];
    },
    [userOptions, assigneeFallbackOption, isAdmin, currentUserOption]
  );

  const contactOptions = useMemo(
    () =>
      contacts.map((c) => ({
        value: c.id,
        label: `${contactDisplayName(c)} [${c.categoryCode}]`,
      })),
    [contacts]
  );

  const leadOptions = useMemo(
    () =>
      leads.map((l) => ({
        value: l.id,
        label: l.label,
      })),
    [leads]
  );

  useEffect(() => {
    if (!open) return;

    if (isEdit && task) {
      form.resetFields();
      form.setFieldsValue({
        title: task.title,
        description: task.description ?? undefined,
        priority: coerceEnumByLabel(task.priority, TASK_PRIORITY_BY_LABEL) ?? 0,
        taskType: coerceEnumByLabel(task.taskType, TASK_TYPE_BY_API_NAME) ?? TASK_TYPE_INTERNAL,
        assigneeId: task.assigneeId ?? undefined,
        contactId: task.contactId ?? undefined,
        leadId: task.leadId ?? undefined,
        dueDate: task.dueDate ? dayjs(task.dueDate) : undefined,
      });
    } else if (!isEdit) {
      form.resetFields();
      form.setFieldsValue({
        priority: 0,
        taskType: defaultTaskType,
        assigneeId: currentUser?.id,
        contactId: initialContactId,
        leadId: initialLeadId,
      });
    }
  }, [isEdit, task, form, open, defaultTaskType, initialContactId, initialLeadId, currentUser?.id]);

  const saving = createMutation.isPending || updateMutation.isPending;

  const handleTaskTypeChange = (value: TaskType) => {
    if (isUnlinkedTaskType(value)) {
      form.setFieldsValue({ contactId: undefined, leadId: undefined });
    } else {
      form.setFieldsValue({
        contactId: contactLocked
          ? initialContactId
          : (initialContactId ?? form.getFieldValue('contactId')),
        leadId: leadLocked
          ? initialLeadId
          : (initialLeadId ?? form.getFieldValue('leadId')),
      });
    }
  };

  const handleContactChange = () => {
    if (!contactLocked) {
      form.setFieldValue('leadId', undefined);
    }
  };

  const handleFinish = (values: Record<string, unknown>) => {
    const dueDate = values.dueDate as dayjs.Dayjs;
    const type =
      coerceEnumByLabel(values.taskType, TASK_TYPE_BY_API_NAME) ?? TASK_TYPE_INTERNAL;
    const payload: TaskFormValues = {
      title: values.title as string,
      description: (values.description as string) ?? undefined,
      priority: coerceEnumByLabel(values.priority, TASK_PRIORITY_BY_LABEL) ?? 0,
      taskType: type,
      assigneeId: values.assigneeId as string,
      contactId: type === TASK_TYPE_FOLLOWUP ? (values.contactId as string) : null,
      leadId: type === TASK_TYPE_FOLLOWUP ? (values.leadId as string) : null,
      dueDate: dueDate.format('YYYY-MM-DD'),
    };

    if (isEdit && editTaskId) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const showFollowupFields = taskType === TASK_TYPE_FOLLOWUP;

  return (
    <Drawer
      title={isEdit ? 'Edit activity' : 'New activity'}
      open={open}
      onClose={onClose}
      width={520}
      destroyOnClose
      placement="right"
    >
      {isEdit && isLoading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{ priority: 0, taskType: defaultTaskType }}
          className="cp-form-section"
        >
          <Form.Item
            name="taskType"
            label="Activity type"
            rules={[{ required: true }]}
            extra="Customer follow-ups are tied to a contact and lead. Internal and recurring tasks are for your team; recurring tasks schedule the next due date when completed."
          >
            <Select
              options={TASK_TYPE_OPTIONS.map((o) => ({
                ...o,
                label: TASK_TYPE_LABELS[o.value as TaskType],
              }))}
              onChange={handleTaskTypeChange}
              disabled={lockContactAndLead}
            />
          </Form.Item>

          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter a title' }]}
          >
            <Input
              placeholder={
                taskType === TASK_TYPE_FOLLOWUP
                  ? 'e.g. Call back about proposal'
                  : taskType === TASK_TYPE_RECURRING
                    ? 'e.g. Monthly pipeline review'
                    : 'e.g. Prepare quarterly review deck'
              }
              maxLength={200}
            />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Optional description" maxLength={2000} />
          </Form.Item>

          <Form.Item name="priority" label="Priority">
            <Select options={TASK_PRIORITY_OPTIONS} />
          </Form.Item>

          <Form.Item
            name="assigneeId"
            label="Assignee"
            rules={[{ required: true, message: 'Please select an assignee' }]}
          >
            <Select
              showSearch
              placeholder={isAdmin ? 'Select a user' : 'Assigned to you'}
              disabled={!isAdmin}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
              options={allUserOptions}
            />
          </Form.Item>

          {showFollowupFields && (
            <>
              <Form.Item
                name="contactId"
                label="Contact"
                rules={[{ required: true, message: 'Please select a contact' }]}
              >
                <Select
                  showSearch
                  placeholder="Select a contact"
                  disabled={contactLocked}
                  onChange={handleContactChange}
                  filterOption={(input, option) =>
                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                  options={contactOptions}
                />
              </Form.Item>

              <Form.Item
                name="leadId"
                label="Lead"
                extra={
                  <Typography.Text type="secondary">
                    Optional. Select a lead to link this follow-up to, or leave empty to link later.
                  </Typography.Text>
                }
              >
                  <Select
                    showSearch
                    allowClear
                    placeholder={
                      contactId
                        ? 'Select a lead (optional)'
                        : 'Select a contact first'
                    }
                    disabled={leadLocked || !contactId}
                    filterOption={(input, option) =>
                      (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={leadOptions}
                  />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="dueDate"
            label="Due Date"
            rules={[{ required: true, message: 'Please select a due date' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              disabledDate={
                isEdit
                  ? undefined
                  : (d) => d !== null && d.isBefore(dayjs().startOf('day'))
              }
            />
          </Form.Item>
        </Form>
      )}

      {!isLoading && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={saving}
          >
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </div>
      )}
    </Drawer>
  );
};

export default TaskFormDrawer;
